import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useReflections } from '../contexts/ReflectionsContext';

export default function ReflectionSheet({ isOpen, onClose, reflection = null }) {
  const { updateReflection, deleteReflection } = useReflections();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    entry: '',
    mood: null
  });

  useEffect(() => {
    if (reflection) {
      setFormData({
        entry: reflection.entry,
        mood: reflection.mood
      });
    } else {
      setFormData({
        entry: '',
        mood: null
      });
    }
  }, [reflection]);

  const moods = [
    { emoji: '😊', name: 'Happy' },
    { emoji: '😌', name: 'Peaceful' },
    { emoji: '🤔', name: 'Thoughtful' },
    { emoji: '😤', name: 'Frustrated' },
    { emoji: '😔', name: 'Sad' },
    { emoji: '🤗', name: 'Grateful' }
  ];

  const handleSubmit = async () => {
    if (!formData.entry.trim() || !formData.mood) return;

    setIsSubmitting(true);
    try {
      await updateReflection(reflection.id, formData);
      onClose();
    } catch (error) {
      console.error('Error updating reflection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!reflection || !window.confirm('Are you sure you want to delete this reflection?')) return;
    
    try {
      await deleteReflection(reflection.id);
      onClose();
    } catch (error) {
      console.error('Error deleting reflection:', error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="flex items-end min-h-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="w-full h-[85vh] transform overflow-hidden bg-white rounded-t-2xl shadow-xl transition-all">
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                  <div className="flex justify-center py-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-300" />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Edit Reflection
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 py-6 space-y-6 overflow-y-auto" style={{ height: 'calc(85vh - 76px)' }}>
                  {/* Mood Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      How were you feeling?
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {moods.map((m) => (
                        <button
                          key={m.name}
                          onClick={() => setFormData(prev => ({ ...prev, mood: m }))}
                          className={`flex flex-col items-center p-4 rounded-xl transition-all ${
                            formData.mood?.name === m.name
                              ? 'bg-primary-50 ring-2 ring-primary-500'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-2xl">{m.emoji}</span>
                          <span className="text-sm font-medium text-gray-600 mt-2">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Entry Text */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Your Reflection
                    </label>
                    <textarea
                      value={formData.entry}
                      onChange={(e) => setFormData(prev => ({ ...prev, entry: e.target.value }))}
                      rows={6}
                      className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 